import { Button, message as adMessage } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { formatFileSize } from "../../../../../utils/shared";
import { FILE_BASE_URL } from "../../../../../config";
import { useCallback, useEffect, useMemo, useState } from "react";
import PrintableImage from "../../../../../components/shared/PrintableImage";
import PdfViewer from "../../../../../components/shared/PdfViewer";
import HtmlViewer from "../../../../../components/shared/HtmlViewer";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedTenantId } from "../../../../../store/user/selectors";
import { updateFileKey } from "../../../../../store/chats/thunks";
import Audio from "../../../../../components/shared/Audio";
import Video from "../../../../../components/shared/Video";
import { handleDownload } from "../../../../../utils/files";

const FileAttachment = ({ attachment, message }) => {
  const [mimeType, setMimeType] = useState(null);

  const tenant_id = useSelector(selectSelectedTenantId);
  const dispatch = useDispatch();

  const { fileUrl, isImage, isAudio, isVideo } = useMemo(() => {
    const isImage = attachment?.mime_type?.startsWith("image");
    const isAudio = attachment?.mime_type?.startsWith("audio");
    const isVideo = attachment?.mime_type?.startsWith("video");

    let fileUrl = null;

    if (attachment?.file) {
      fileUrl = URL.createObjectURL(attachment.file);
    } else if (attachment?.storage_url) {
      fileUrl = `${FILE_BASE_URL}${attachment.storage_url}`;
    }

    return { fileUrl, isImage, isAudio, isVideo };
  }, [attachment]);
  useEffect(() => {
    return () => {
      if (attachment?.file && fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [attachment?.file, fileUrl]);

  const onError = useCallback(async () => {
    return await dispatch(
      updateFileKey({
        attachment_id: attachment?.id,
        tenant_id,
        message_id: message?.id,
        conversation_id: message?.conversation_id,
      }),
    );
  }, [message, tenant_id, attachment]);

  const downloadWithFallback = useCallback(async () => {
    try {
      // First attempt
      const failed = await handleDownload(fileUrl, attachment?.filename);

      if (!failed) return;

      const { payload } = await onError();

      if (!payload?.url) throw new Error("URL Not Found");

      const retryFailed = await handleDownload(
        `${FILE_BASE_URL}${payload.url}`,
        attachment?.filename,
      );

      if (retryFailed) throw new Error("URL Not Found");
    } catch (error) {
      adMessage.error("Download failed. Please try again after some time.");
    }
  }, [fileUrl, attachment]);

  return (
    <div className="attachment-container">
      {isImage ? (
        <div className="image-preview">
          <PrintableImage
            key={attachment?.id}
            src={fileUrl}
            alt={attachment.filename}
            onError={onError}
          />
          <div className="download-overlay" onClick={downloadWithFallback}>
            <span className="download-icon">
              <DownloadOutlined />
            </span>
          </div>
        </div>
      ) : isAudio ? (
        <Audio
          key={attachment?.id}
          fileUrl={fileUrl}
          attachment={attachment}
          onError={onError}
        />
      ) : isVideo ? (
        <Video
          key={attachment?.id}
          fileUrl={fileUrl}
          attachment={attachment}
          onError={onError}
        />
      ) : (
        <div
          className="file-attachment"
          onClick={() => {
            const mime = attachment?.mime_type || "";
            if (mime.includes("pdf")) {
              setMimeType("pdf");
            } else if (mime.includes("html")) {
              setMimeType("html");
            }
          }}
        >
          <div className="file-icon">📄</div>
          <div className="file-info">
            <div className="file-name">{attachment.filename}</div>
            <div className="file-size">
              {formatFileSize(attachment.file_size_bytes)}
            </div>
          </div>
          <Button
            className="download-button"
            onClick={(e) => {
              e.stopPropagation();
              downloadWithFallback();
            }}
            icon={<DownloadOutlined />}
            shape="circle"
          />
        </div>
      )}
      {mimeType === "pdf" ? (
        <PdfViewer
          key={attachment?.id}
          pdfUrl={fileUrl}
          onClose={() => setMimeType(null)}
          onError={onError}
        />
      ) : mimeType === "html" ? (
        <HtmlViewer
          key={attachment?.id}
          url={fileUrl}
          onClose={() => setMimeType(null)}
          onError={onError}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default FileAttachment;
